import { call, all, fork, put, takeLatest, select } from 'redux-saga/effects';
import { isolateMosaicLayers } from '../utils/layerData/mosaic';
import { extractSoilSsurgo } from '../utils/layerData/mosaic/soilSsurgo';
import { logDmbErrorLocation } from '../utils/error';
import {
  REQUEST_SOIL_SSURGO_ZONES,
  REQUEST_IMPORTED_SHAPEFILE_DATA,
} from '../constants/actionTypes';

import {
  receiveSoilSsurgoZones,
  soilSsurgoZonesFailure,
  receiveImportedShapefileData,
  importedShapefileDataFailure,
  getMosaicLayersFailure
} from '../actions/mosaicActions';

import {
  requestMosaicV3Layers,
  requestSoilSsurgoLatest,
  requestSoilSsurgoZones,
  requestImportedShapefileData,
} from '../requesters/mosaicRequester';

export function* getSoilSsurgo(fieldId, userId) {
  try {
    const ssurgo = yield call(requestSoilSsurgoLatest, fieldId, userId);
    return ssurgo;
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getSoilSsurgo');
    return {};
  }
}

export function* getMosaicV3Layers(fieldId, userId) {
const state = yield select();
const webMicrosoftImageryFF = state.feature.userConfigFeatures["web.microsoft-imagery"].value;
  try {
    const mosaicGeneric = yield call(requestMosaicV3Layers, fieldId, userId, webMicrosoftImageryFF);
    return mosaicGeneric;
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getMosaicV3Layers');
    return yield put(getMosaicLayersFailure());
  }
}

export function* getMicrosoftAndMosaicV3Layers(fieldId, userId) {
  const state = yield select();
  const webMicrosoftImageryFF = state.feature.userConfigFeatures["web.microsoft-imagery"].value;
  try {
    const mosaicGeneric = yield call(requestMosaicV3Layers, fieldId, userId, webMicrosoftImageryFF);
    return mosaicGeneric;
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getMosaicV3Layers');
    return yield put(getMosaicLayersFailure());
  }
}

/**
 * Calls the requester to get mosaic v3 layers
 * @param {String} fieldId
 * @param {Number} userId
 * @return {Object}
 */
export function* getMosaicData(fieldId, userId) {
  const state = yield select();
  const webMicrosoftImageryFF = state.feature.userConfigFeatures["web.microsoft-imagery"].value;

  const [mosaicGeneric, ssurgo] = yield all([
    webMicrosoftImageryFF 
      ? call(getMicrosoftAndMosaicV3Layers, fieldId, userId) 
      : call(getMosaicV3Layers, fieldId, userId),
    call(getSoilSsurgo, fieldId, userId)
  ]);

  const { seasonAgnostic, seasonSpecific } = isolateMosaicLayers(
    mosaicGeneric.data
  );
  const ssurgoSeasonData = extractSoilSsurgo(ssurgo.data);

  return {
    seasonSpecific,
    seasonAgnostic: { ...seasonAgnostic, ...ssurgoSeasonData },
  };
};

/**
 * Calls the requester to get the imported shapefile geoJson with specified zones
 * @param {Action} action
 * @return {Object}
 */
export function* getImportedShapefileData(action) {
  try {
    const response = yield call(requestImportedShapefileData, action);
    yield put(receiveImportedShapefileData(response));
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getImportedShapefileZones');
    yield put(importedShapefileDataFailure());
  }
}

/**
 * Calls the requester to get the soil SSURGO geoJson with specified zones
 * @param {Object} action
 * @param {String} action.imageId
 * @return {Object}
 */
export function* getSoilSsurgoZones(action) {
  try {
    const response = yield call(requestSoilSsurgoZones, action.imageId);
    yield put(receiveSoilSsurgoZones(response.features));
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga');
    yield put(soilSsurgoZonesFailure());
  }
}

/**
 * Waits for the return of the REQUEST_IMPORTED_SHAPEFILE_DATA action event
 * and calls the getImportedShapefileData
 * @return {void}
 */
export function* getImportedShapefilesDataWatcher() {
  yield takeLatest(REQUEST_IMPORTED_SHAPEFILE_DATA, getImportedShapefileData);
}

export function* getSoilSsurgoZonesWatcher() {
  yield takeLatest(REQUEST_SOIL_SSURGO_ZONES, getSoilSsurgoZones);
}

export default function* rootSaga() {
  yield fork(getImportedShapefilesDataWatcher);
  yield fork(getSoilSsurgoZonesWatcher);
}
